import type { ReactNode } from 'react';

/**
 * Gender options enum for travellers
 */
export enum GenderEnum {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

/**
 * How a child's age is captured: an exact birthdate, or an age the birthdate
 * is only estimated from.
 */
export enum ChildAgeModeEnum {
  DATE = 'date',
  AGE = 'age',
}

/**
 * Child entry mode and details in the folder form
 */
export interface ChildEntry {
  mode: ChildAgeModeEnum;
  birthdate: string | null;
  age: number | null;
  ageIsExact?: boolean;
}

/**
 * Detail fields for an individual traveller (adult or child)
 */
export interface TravellerDetail {
  id?: string;
  kind?: 'adult' | 'child';
  position?: number;
  gender?: string;
  firstName?: string;
  lastName?: string;
  nationality?: string;
  passportNumber?: string;
  passportExpirationDate?: string;
  birthdate?: string;
  age?: number;
  ageIsExact?: boolean;
}

/**
 * Room composition pax allocation
 */
export interface RoomPax {
  adults: number;
  children: number;
  childrenBirthdates?: string[];
}

/**
 * Complete form values structure for Folder creation/edition
 */
export interface FolderFormValues {
  id?: string;
  name: string;
  icr: string;
  locale: string;
  /**
   * Folder fields the DMC edits in its Additional Details section and the TO
   * never shows. They belong to the folder contract (AddFolder / EditFolderBody),
   * so the form has to carry them either way: dropped from the payload, the API
   * overwrites the stored value with nothing.
   */
  tourOperatorName?: string;
  networkName?: string;
  note?: string;
  adults: number;
  children: ChildEntry[];
  rooms: RoomPax[];
  travelers: TravellerDetail[];
}

/**
 * Props for FolderCreateModalForm component
 */
export interface FolderCreateModalFormProps {
  onSubmit: (values: any) => void;
  isPending?: boolean;
  onImport?: (values: any) => void;
  onCancel?: () => void;
  hideImportButton?: boolean;
  canEditIcr?: boolean;
  initialValues?: any;
  renderTextField?: (props: any) => ReactNode;
  renderSelectField?: (props: any) => ReactNode;
  renderDateField?: (props: any) => ReactNode;
  renderNationalityField?: (props: any) => ReactNode;
  renderCheckboxField?: (props: any) => ReactNode;
  renderLanguageField?: (props: any) => ReactNode;
  extraFields?: (props: any) => ReactNode;
  submitLabel?: string;
  renderSubmitArea?: (props: any) => ReactNode;
  /**
   * Receives a close handler that raises the discard confirmation when the
   * form holds unsaved changes. Wire it to Esc / overlay closing.
   */
  onRegisterCloseGuard?: (guard: () => void) => void;
  /**
   * Makes the traveller details section a prerequisite instead of an optional
   * step: the folder cannot be saved while a pax is still incomplete.
   */
  requireCompleteTravellers?: boolean;
}

/**
 * Props for FolderTravellersSection component (Group size & Room composition)
 */
export interface FolderTravellersSectionProps {
  values: FolderFormValues;
  setFieldValue: (field: string, value: any) => void;
  t: (key: string, opts?: any) => string;
}

/**
 * Props for FolderTravellerDetailsSection component (Collapsible traveller cards)
 */
export interface FolderTravellerDetailsSectionProps {
  values: FolderFormValues;
  setFieldValue: (field: string, value: any) => void;
  renderTextField?: (name: string, label: string) => ReactNode;
  renderSelectField?: (
    name: string,
    label: string,
    options: { label: string; value: string }[],
  ) => ReactNode;
  renderDateField?: (name: string, label: string) => ReactNode;
  renderNationalityField?: (name: string, label: string) => ReactNode;
  t: (key: string, opts?: any) => string;
  /** Travellers are mandatory: state it in the section description. */
  requireCompleteTravellers?: boolean;
}

/**
 * Props for TravellerFields sub-component inside FolderTravellerDetailsSection
 */
export interface TravellerFieldsProps extends Pick<
  FolderTravellerDetailsSectionProps,
  | 'setFieldValue'
  | 'renderTextField'
  | 'renderSelectField'
  | 'renderDateField'
  | 'renderNationalityField'
  | 't'
> {
  index: number;
  detail: TravellerDetail;
  birthdate: string;
  birthdateNote?: string;
  birthdateNoteTone?: 'muted' | 'warning';
}

/**
 * Props for FolderChildrenAgesField component
 */
export interface FolderChildrenAgesFieldProps {
  entries: ChildEntry[];
  onBirthdateChange: (index: number, date: Date | null) => void;
  onAgeChange: (index: number, age: number | null) => void;
  onModeChange: (index: number, mode: 'date' | 'age') => void;
  t: (key: string, options?: Record<string, any>) => string;
}

/**
 * Props for FolderFooterHint component
 */
export interface FolderFooterHintProps {
  values: FolderFormValues;
  serverError?: string | null;
  t: (key: string, opts?: any) => string;
  /** Incomplete travellers block the save: show the hint as a blocker. */
  requireCompleteTravellers?: boolean;
}
